import { NonIdealState } from '@blueprintjs/core';
import * as React from 'react';

import type { GroupType } from 'vms/generated/groups';

import { useCurrentUser } from './queries';
import type { User } from './types';

export type { GroupType } from 'vms/generated/groups';

export const hasPermission = (user: User | null | undefined, group: GroupType) =>
  Boolean(user?.groups?.includes(group));

export const useHasPermission = () => {
  const groups = useCurrentUser()?.groups;
  return React.useCallback(
    (...requiredGroups: GroupType[]) => {
      if (!requiredGroups.length) {
        return true;
      }
      for (const group of requiredGroups) {
        if (groups?.includes(group)) {
          return true;
        }
      }
      return false;
    },
    [groups]
  );
};

export type HasPermissionProps = {
  fallback?: React.ReactNode;
  children: React.ReactNode;
} & { [key in GroupType]?: boolean };

export const HasPermission: React.FC<HasPermissionProps> = ({
  children,
  fallback = null,
  ...groups
}) => {
  const hasPerm = useHasPermission();
  if (!Object.keys(groups).length) {
    return <>{children}</>;
  }
  for (const pair of Object.entries(groups)) {
    if (!pair[1]) {
      continue;
    }
    if (hasPerm(pair[0] as GroupType)) {
      return <>{children}</>;
    }
  }
  return <>{fallback}</>;
};

export function checkPerm<P extends object>(
  Component: React.ComponentType<P>,
  ...groups: GroupType[]
) {
  const Wrapped: React.FC<P> = (props: P) => {
    const hasPerm = useHasPermission();
    if (!hasPerm(...groups)) {
      return null;
    }
    return <Component {...props} />;
  };
  return Wrapped;
}

// Page guards retain a visible denial; optional controls use HasPermission directly.
export function PermissionGate(props: Omit<HasPermissionProps, 'fallback'>) {
  return (
    <HasPermission
      {...props}
      fallback={
        <NonIdealState
          title="Access denied"
          description="You do not have permission to access this page."
        />
      }
    />
  );
}
